import React, { useState, useEffect } from 'react';
import { Card, Select, Typography, Space, DatePicker, Tooltip, Empty, Spin, Radio } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  ResponsiveContainer, Legend, ReferenceLine, Area,
  ComposedChart, Scatter, Brush, Label
} from 'recharts';
import dayjs from '../../../dayjs';
import OTRDataService from '../utils/OTRDataService';
import { OTR_THRESHOLDS } from '../utils/OTRAnalytics';
import { toHrsMinsSecs } from '../../../libs/formatLib';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * Statistical Trip Variance Chart
 * Displays mean/average variance with standard deviation bands
 * across multiple dates for better visualization of trends
 */
const OTRStatisticalVarianceChart = ({
                                       selectedTrip,
  title = "Trip Variance Statistical Analysis",
  defaultRange = 30 // default to 30 days
}) => {
    const [data, setData] = useState([]);
  const [statisticalData, setStatisticalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('mean'); // 'mean' or 'median'
  const [confidenceInterval, setConfidenceInterval] = useState(0.95); // 95% confidence interval

  // Generate sequence numbers for stops
  const generateStopSequence = (data, maxStops = 50) => {
    if (!data || data.length === 0) return [];
    
    // First try to use any existing sequence data (STOP_SEQUENCE, TIME_RECORD_SEQUENCE, etc.)
    const sequenceField = data[0]?.STOP_SEQUENCE !== undefined ? 'STOP_SEQUENCE' : 
                        data[0]?.TIME_RECORD_SEQUENCE !== undefined ? 'TIME_RECORD_SEQUENCE' :
                        data[0]?.SEQUENCE_NUMBER !== undefined ? 'SEQUENCE_NUMBER' : null;
    
    if (sequenceField) {
      // Extract unique stop sequence numbers
      const stopSequences = [...new Set(
        data.map(item => Number(item[sequenceField]))
          .filter(seq => !isNaN(seq) && seq > 0 && seq <= maxStops)
      )];
      
      // Sort numerically
      return stopSequences.sort((a, b) => a - b);
    }
    
    // If we don't have sequence numbers, create sequence based on scheduled times
    const sortedData = [...data].sort((a, b) => {
      const timeA = a.SCHEDULED_ARRIVAL_TIME || a.SCHEDULED_DEPARTURE_TIME || 0;
      const timeB = b.SCHEDULED_ARRIVAL_TIME || b.SCHEDULED_DEPARTURE_TIME || 0;
      return timeA - timeB;
    });
    
    // Create artificial sequence numbers
    return Array.from(Array(sortedData.length).keys()).map(i => i + 1);
  };

  // Process and prepare statistical data
  const processStatisticalData = (inputData) => {
    setLoading(true);
    
    try {
      console.log("Processing statistical data with", inputData.length, "records");
      
      // Debug variance data availability
      const varianceFields = [
        'DEPARTURE_TIME_VARIANCE', 
        'departure_variance', 
        'ARRIVAL_TIME_VARIANCE', 
        'arrival_variance', 
        'variance'
      ];
      
      const hasVarianceData = inputData.some(item => 
        varianceFields.some(field => 
          item[field] !== undefined && item[field] !== null && typeof item[field] === 'number'
        )
      );
      
      console.log("Data has variance fields:", hasVarianceData);
      
      if (inputData.length > 0) {
        const sampleRecord = inputData[0];
        console.log("Sample record:", sampleRecord);
        console.log("Available fields:", Object.keys(sampleRecord));
        console.log("Variance fields in sample:", 
          varianceFields.map(field => ({ field, value: sampleRecord[field], type: typeof sampleRecord[field] }))
        );
        console.log("Sequence fields in sample:",
          ['STOP_SEQUENCE', 'TIME_RECORD_SEQUENCE', 'SEQUENCE_NUMBER'].map(field => 
            ({ field, value: sampleRecord[field], type: typeof sampleRecord[field] })
          )
        );
      }
      
      // If no data after filtering, return
      if (inputData.length === 0) {
        setStatisticalData([]);
        setLoading(false);
        return;
      }
      
      // Generate stop sequence
      const stopSequence = generateStopSequence(inputData);
      
      console.log("Generated stop sequence:", stopSequence);
      
      // Calculate statistics by stop sequence
      const statisticsByStop = {};
      
      // Group records by stop sequence
      // If no stop sequence field exists, group by stop number or create artificial sequence
      inputData.forEach(item => {
        // Try different possible sequence fields
        const sequence = item.STOP_SEQUENCE !== undefined ? Number(item.STOP_SEQUENCE) :
                       item.TIME_RECORD_SEQUENCE !== undefined ? Number(item.TIME_RECORD_SEQUENCE) :
                       item.SEQUENCE_NUMBER !== undefined ? Number(item.SEQUENCE_NUMBER) : null;
        
        if (sequence === null) {
          console.log("Missing sequence number in record:", item);
          return;
        }
        
        if (!statisticsByStop[sequence]) {
          statisticsByStop[sequence] = {
            values: []
          };
        }
        
        // Extract variance values - try different fields
        let variance = null;
        if (item.DEPARTURE_TIME_VARIANCE !== undefined && item.DEPARTURE_TIME_VARIANCE !== null) {
          variance = item.DEPARTURE_TIME_VARIANCE;
        } else if (item.departure_variance !== undefined && item.departure_variance !== null) {
          variance = item.departure_variance;
        } else if (item.ARRIVAL_TIME_VARIANCE !== undefined && item.ARRIVAL_TIME_VARIANCE !== null) {
          variance = item.ARRIVAL_TIME_VARIANCE;
        } else if (item.arrival_variance !== undefined && item.arrival_variance !== null) {
          variance = item.arrival_variance;
        } else if (item.variance !== undefined && item.variance !== null) {
          variance = item.variance;
        } else if (item.chartData && item.chartData.variance !== undefined) {
          variance = item.chartData.variance;
        }
        
        // If we found a variance value, add it to the sequence group
        if (variance !== null && typeof variance === 'number' && !isNaN(variance)) {
          statisticsByStop[sequence].values.push(variance);
        }
      });
      
      // Log statistics for debugging
      console.log("Statistics by stop:", Object.keys(statisticsByStop).length, "sequences found");
      Object.keys(statisticsByStop).forEach(seq => {
        console.log(`Sequence ${seq}: ${statisticsByStop[seq].values.length} variance values`);
      });
      
      // Calculate statistics for each stop
      Object.keys(statisticsByStop).forEach(seq => {
        const values = statisticsByStop[seq].values;
        
        if (values.length === 0) {
          console.log("No variance values for stop sequence", seq);
          return;
        }
        
        // Calculate mean
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        
        // Calculate standard deviation
        const squaredDifferences = values.map(val => Math.pow(val - mean, 2));
        const variance = squaredDifferences.reduce((sum, val) => sum + val, 0) / values.length;
        const stdDev = Math.sqrt(variance);
        
        // Calculate median
        const sortedValues = [...values].sort((a, b) => a - b);
        const median = sortedValues.length % 2 === 0 
          ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
          : sortedValues[Math.floor(sortedValues.length / 2)];
        
        // Calculate confidence interval multiplier (z-score)
        let zScore;
        if (confidenceInterval === 0.99) zScore = 2.576;
        else if (confidenceInterval === 0.90) zScore = 1.645;
        else zScore = 1.96; // Default 95%
        
        const upperBound = mean + (zScore * stdDev);
        const lowerBound = mean - (zScore * stdDev);
        
        // Store calculated statistics
        statisticsByStop[seq].mean = mean;
        statisticsByStop[seq].median = median;
        statisticsByStop[seq].stdDev = stdDev;
        statisticsByStop[seq].upperBound = upperBound;
        statisticsByStop[seq].lowerBound = lowerBound;
        statisticsByStop[seq].sampleSize = values.length;
      });
      
      // Prepare chart data - if no stop sequence was generated, use the keys from statisticsByStop
      const sequences = stopSequence.length > 0 ? stopSequence : 
                      Object.keys(statisticsByStop).map(Number).sort((a, b) => a - b);
      
      // Create chart data array
      const chartData = sequences.map(seq => {
        const stats = statisticsByStop[seq] || { 
          mean: 0, median: 0, stdDev: 0, upperBound: 0, lowerBound: 0, sampleSize: 0 
        };
        
        return {
          stopSequence: seq,
          mean: stats.mean || 0,
          median: stats.median || 0,
          upperBound: stats.upperBound || 0,
          lowerBound: stats.lowerBound || 0,
          stdDev: stats.stdDev || 0,
          sampleSize: stats.sampleSize || 0
        };
      });
      
      console.log("Generated statistical chart data with", chartData.length, "points");
      
      // Filter out any points with no data
      const validChartData = chartData.filter(point => point.sampleSize > 0);
      
      if (validChartData.length === 0) {
        console.warn("No valid data points after processing");
      }
      
      setStatisticalData(validChartData);
    } catch (error) {
      console.error("Error processing statistical data:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const load = async () => {
      console.log(`Getting trip data for trip ${selectedTrip.tripId}`);
      // Get raw data directly from data service
      const allData = await OTRDataService.retrieveAllData();
      // Filter for just the current trip
      const tripRecords = allData.filter(r => r.TRIP_ID === selectedTrip.tripId);
      console.log(`Sending ${tripRecords.length} records to OTRStatisticalVarianceChart`);

      if (tripRecords && tripRecords.length > 0) {
        console.log("Data received in OTRStatisticalVarianceChart:", tripRecords.length, "records");
        processStatisticalData(tripRecords);
      }
    }
    load().then(() => console.log("OTRStatisticalVarianceChart loaded"));
  }, [selectedTrip]);

  // Handle view mode change
  const handleViewModeChange = (e) => {
    setViewMode(e.target.value);
  };
  
  // Handle confidence interval change
  const handleConfidenceIntervalChange = (value) => {
    setConfidenceInterval(parseFloat(value));
  };
  
  // Render statistical chart
  const renderChart = () => {
    if (loading) {
      return <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin size="large" /></div>;
    }
    
    if (!statisticalData || statisticalData.length === 0) {
      return <Empty description="No data available" style={{ height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }} />;
    }
    
    const centralMeasure = viewMode === 'mean' ? 'mean' : 'median';
    
    // Calculate YAxis ticks at every minute mark, similar to OTRTripChart
    const variances = statisticalData.flatMap(d => [d.upperBound, d.lowerBound, d[centralMeasure]]).filter(v => typeof v === 'number');
    const minVar = Math.min(...variances);
    const maxVar = Math.max(...variances);
    const minTick = Math.floor(minVar / 60) * 60;
    const maxTick = Math.max(Math.ceil(maxVar / 60) * 60, 120); // Ensure at least 2m (120s)
    const ticks = [];
    for (let t = minTick; t <= maxTick; t += 60) {
      ticks.push(t);
    }
    
    return (
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={statisticalData}
          margin={{ top: 20, right: 30, left: 5, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="stopSequence" 
            label={{ value: 'Stop Sequence', position: 'insideBottomRight', offset: 0 }}
          />
          <YAxis 
            label={{ value: 'Variance (minutes)', angle: -90, position: 'insideLeft' }} 
            domain={[minTick, maxTick]} 
            tickFormatter={(value) => toHrsMinsSecs(value)}
            ticks={ticks}
          />
          <Tooltip
            formatter={(value, name) => {
              if (name === 'Mean' || name === 'Median') return [value.toFixed(1) + ' min', name];
              if (name === 'Standard Deviation Band') {
                const stdDev = statisticalData[0]?.stdDev || 0;
                return [`+/- ${stdDev.toFixed(1)} min`, name];
              }
              return [value.toFixed(1) + ' min', name];
            }}
            labelFormatter={(label) => `Stop ${label}`}
          />
          <Legend />
          
          {/* Standard Deviation Band */}
          <Area 
            type="monotone" 
            dataKey="upperBound" 
            stroke="none" 
            fillOpacity={0.2} 
            fill="#8884d8" 
          />
          <Area 
            type="monotone" 
            dataKey="lowerBound" 
            stroke="none" 
            fillOpacity={0.2} 
            fill="#0084d8" 
          />
          
          {/* Central Measure Line (Mean or Median) */}
          <Line 
            type="monotone" 
            dataKey={centralMeasure} 
            stroke="#8884d8" 
            strokeWidth={3} 
            dot={{ r: 4 }} 
            activeDot={{ r: 8 }} 
            name={viewMode === 'mean' ? 'Mean' : 'Median'} 
          />
          
          {/* Reference Lines */}
          <ReferenceLine y={OTR_THRESHOLDS.onTime} stroke="#666" strokeDasharray="3 3">
            <Label value="On Time" position="insideTopRight" />
          </ReferenceLine>
          <ReferenceLine
            y={OTR_THRESHOLDS.early}
            stroke="red"
            strokeDasharray="3 3"
            label={{ value: 'Early', position: 'insideTopRight', fill: 'red' }}
          />
          <ReferenceLine
            y={OTR_THRESHOLDS.late}
            stroke="blue"
            strokeDasharray="3 3"
            label={{ value: 'Late', position: 'insideBottomRight', fill: 'blue' }}
          />
          
          {/* Brush for zooming */}
          {/* <Brush dataKey="stopSequence" height={30} stroke="#8884d8" /> */}
        </ComposedChart>
      </ResponsiveContainer>
    );
  };
  
  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>{title}</Title>
        </div>
      }
      extra={
        <Space>
          <Tooltip title="This chart displays statistical analysis of trip variance data across multiple dates. It shows the mean/median with standard deviation bands to represent data variability.">
            <InfoCircleOutlined style={{ fontSize: 16 }} />
          </Tooltip>
        </Space>
      }
    >
      <div style={{ marginBottom: 20 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space wrap>
            <Text strong>View Mode:</Text>
            <Radio.Group value={viewMode} onChange={handleViewModeChange}>
              <Radio.Button value="mean">Mean</Radio.Button>
              <Radio.Button value="median">Median</Radio.Button>
            </Radio.Group>
            
            <Text strong>Confidence:</Text>
            <Select 
              value={confidenceInterval} 
              onChange={handleConfidenceIntervalChange}
              style={{ width: 100 }}
            >
              <Option value={0.90}>90%</Option>
              <Option value={0.95}>95%</Option>
              <Option value={0.99}>99%</Option>
            </Select>
          </Space>
          
          <Text type="secondary">
            {viewMode === 'mean' ? 'Showing mean values with standard deviation bands' : 'Showing median values with standard deviation bands'}.
            {` Filtered by main dashboard filters. Confidence interval: ${confidenceInterval * 100}%`}
          </Text>
        </Space>
      </div>
      
      {renderChart()}
      
      <div style={{ marginTop: 16 }}>
        <Text type="secondary">
          <strong>Note:</strong> This chart shows the {viewMode === 'mean' ? 'mean (average)' : 'median'} variance across all trips with 
          {` ${confidenceInterval * 100}% `}
          confidence interval bands (±{viewMode === 'mean' && statisticalData[0]?.stdDev ? statisticalData[0].stdDev.toFixed(1) : '0'} minutes).
          Performance is considered on-time between {(OTR_THRESHOLDS.early/60).toFixed(0)} (early) and {(OTR_THRESHOLDS.late/60).toFixed(0)} (late).
        </Text>
      </div>
    </Card>
  );
};

export default OTRStatisticalVarianceChart; 